// BrandMenu — the "ArchLang Studio" wordmark dropdown.
// Workspace switcher + create, Settings modal, Sign out.
// Built on AppBrandMenu (shared with viewer's SourceMenu).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AppBrandMenu,
  type AppBrandMenuItem,
  type AppBrandMenuSection,
  useOutOfHostMenuItems,
} from "@archlang/app-shell/editor";
import { MenuItem } from "../ui/Menu";
import { Avatar } from "../ui/Avatar";
import { apiFetch } from "../api/client";
import { ME_KEY } from "../api/hooks/useMe";
import { useWorkspaces } from "../api/hooks/useWorkspaces";
import { CreateWorkspaceModal } from "../workspace/CreateWorkspaceModal";
import { wsHome } from "../lib/paths";
import { useOpenSettings } from "../settings/useOpenSettings";
import type { Workspace } from "../api/types";

const PLUS_ICON = (
  <svg className="h-[16px] w-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const GEAR_ICON = (
  <svg className="h-[16px] w-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 15a3 3 0 100-6 3 3 0 000 6zm7.4-3a7.4 7.4 0 00-.13-1.36l2-1.55-2-3.46-2.36.95a7.4 7.4 0 00-2.35-1.36L14.2 2h-4l-.36 2.86a7.4 7.4 0 00-2.35 1.36l-2.36-.95-2 3.46 2 1.55A7.4 7.4 0 004.6 12c0 .46.05.91.13 1.36l-2 1.55 2 3.46 2.36-.95c.7.57 1.49 1.04 2.35 1.36L10.2 22h4l.36-2.86a7.4 7.4 0 002.35-1.36l2.36.95 2-3.46-2-1.55c.08-.45.13-.9.13-1.36z" />
  </svg>
);

const SIGN_OUT_ICON = (
  <svg className="h-[16px] w-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 16l4-4-4-4M19 12H9M9 4H6a2 2 0 00-2 2v12a2 2 0 002 2h3" />
  </svg>
);

const CHECK_ICON = (
  <svg className="h-4 w-4 shrink-0 text-arch-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

interface BrandMenuProps {
  readonly slug: string | null;
}

export function BrandMenu({ slug }: BrandMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaces = useWorkspaces();
  const [creating, setCreating] = useState(false);
  const openSettings = useOpenSettings("profile");
  // Contributed app-menu items (static plugin `menuItems` + the in-host dynamic
  // export/share items), resolved + when-gated out-of-host via the registry
  // bridge. Grouped into sections by `group`, preserving order.
  const menuItems = useOutOfHostMenuItems();
  const contributedSections: AppBrandMenuSection[] = [];
  {
    const byGroup = new Map<string, AppBrandMenuItem[]>();
    const order: string[] = [];
    for (const it of menuItems) {
      const g = it.group ?? "";
      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
      byGroup.get(g)!.push({
        key: it.id,
        icon: it.icon,
        label: it.label,
        variant: it.danger ? ("danger" as const) : undefined,
        disabled: it.disabled,
        onClick: it.run,
      });
    }
    for (const g of order) contributedSections.push({ label: g || undefined, items: byGroup.get(g)! });
  }

  const orderedWorkspaces: readonly Workspace[] = [...(workspaces.data ?? [])].sort(
    (a, b) => Number(b.isPersonal) - Number(a.isPersonal) || a.name.localeCompare(b.name),
  );

  async function handleSignOut() {
    try {
      await apiFetch("/auth/sign-out", { method: "POST" });
    } catch {
      // Best-effort.
    }
    queryClient.clear();
    queryClient.setQueryData(ME_KEY, null);
    void navigate("/sign-in");
  }

  // Workspace switcher — show when >1 workspace or on slug-less routes.
  const showWsList = orderedWorkspaces.length > 1 || !slug;

  const wsItems: AppBrandMenuItem[] = showWsList
    ? orderedWorkspaces.map((w) => ({
        kind: "custom" as const,
        key: w.id,
        render: () => (
          <MenuItem
            onClick={() => void navigate(wsHome(w.slug))}
            className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-slate-800/60 focus-visible:bg-slate-800/60 focus-visible:outline-none ${w.slug === slug ? "text-slate-100" : "text-slate-300"}`}
          >
            <Avatar name={w.name} size="sm" shape="square" />
            <span className="flex-1 truncate text-left">{w.name}</span>
            {w.isPersonal && (
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">personal</span>
            )}
            {w.slug === slug && CHECK_ICON}
          </MenuItem>
        ),
      }))
    : [];

  wsItems.push({
    key: "create",
    icon: PLUS_ICON,
    label: "Create workspace",
    variant: "accent",
    onClick: () => setCreating(true),
  });

  return (
    <>
      <AppBrandMenu
        tagline="Studio"
        sections={[
          ...(wsItems.length > 0
            ? [{ label: showWsList ? "Workspaces" : undefined, items: wsItems }]
            : []),
          ...contributedSections,
          {
            items: [
              { key: "settings", icon: GEAR_ICON, label: "Settings", onClick: openSettings },
            ],
          },
          {
            items: [
              { key: "signout", icon: SIGN_OUT_ICON, label: "Sign out", variant: "danger" as const, onClick: () => void handleSignOut() },
            ],
          },
        ]}
      />
      <CreateWorkspaceModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(w) => {
          setCreating(false);
          void navigate(wsHome(w.slug));
        }}
      />
    </>
  );
}
